import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/UserContext';
import { useJournalStore, useJournalStoreBase } from '../stores/journalStore';
import { useAccountStore } from '../stores/accountStore';
import { ConfirmModal, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, BulkSelectCell, ListPagination } from '@redpanda/ui';
import JournalDialog from '../components/JournalDialog';
import InvoiceLimitBanner from '../components/InvoiceLimitBanner';
import { useInvoiceLimitCache } from '../hooks/useInvoiceLimitCache';
import { toast } from 'react-hot-toast';
import { useBulkActions, createBulkDeleteOptimisticHandler, createOptimisticBulkDeleteUpdate, createBulkDeleteSuccessHandler, isAdmin, canDeleteItem } from '@redpanda/utils';
import BulkActionButton from '../components/BulkOperations/BulkActionButton';
import BulkSelectHeader from '../components/BulkOperations/BulkSelectHeader';
import { TableSkeleton } from '@redpanda/ui';
import { bulkDeleteJournalEntries, getAccounts, bulkPostJournalEntries, bulkUnpostJournalEntries } from '../services/api';

const JournalListPage: React.FC = () => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const { isAuthenticated, user, isLoading: isUserLoading } = userContext!;
  const currentUserId = user?.id;
  const userRole = user?.role;
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedJournal, setSelectedJournal] = useState<any>(null);
  const [canAddJournal, setCanAddJournal] = useState(true);

  // 使用發票限制快取 hook
  const {
    canAddInvoice,
    setCanAddInvoice,
    limitBannerRefreshTrigger,
    setLimitBannerRefreshTrigger,
  } = useInvoiceLimitCache();

  // 批量過帳和取消過帳狀態
  const [isBulkPosting, setIsBulkPosting] = useState(false);
  const [isBulkUnposting, setIsBulkUnposting] = useState(false);
  const [showBulkPostModal, setShowBulkPostModal] = useState(false);
  const [showBulkUnpostModal, setShowBulkUnpostModal] = useState(false);


  const {
    journals,
    pagination,
    isLoading,
    showDeleteConfirmModal,
    isDeleting,
    setCurrentPage,
    fetchJournals,
    handleDelete: deleteJournal,
    confirmDelete,
    closeDeleteModal,
    updateJournal,
    postJournal,
    unpostJournal,
    postingStates,
  } = useJournalStore();
  const { refreshData: refreshAccounts } = useAccountStore();


  const bulkActions = useBulkActions({
    items: journals ?? [],
    idField: 'id',
    entityName: t('journals.item'),
    bulkDeleteApi: bulkDeleteJournalEntries,
    pagination: pagination ? {
      currentPage: pagination.currentPage,
      totalPages: pagination.totalPages
    } : undefined,
    fetchItems: fetchJournals,
    onOptimisticUpdate: createBulkDeleteOptimisticHandler(
      useJournalStoreBase.setState,
      'journal',
      () => journals ?? [],
      () => pagination
    ),
    onSuccess: async (deletedIds) => {
      const handler = createBulkDeleteSuccessHandler(
        () => useJournalStoreBase.getState(),
        fetchJournals,
        setCurrentPage
      );
      await handler(deletedIds);
      refreshAccounts(); // 刷新會計科目列表以更新使用計數
    },
    userRole: userRole,
    t,
    toast,
    setCurrentPage,
    // 移除 canDeleteItem 限制，讓所有分錄都可以被選取
    // 刪除時的限制由 API 端處理
  });

  const {
    selectedItems,
    toggleItemSelection,
    toggleSelectAll,
    showBulkDeleteModal,
    isBulkDeleting,
    handleBulkDelete,
    closeBulkDeleteModal,
    allSelected,
    setSelectedItems,
  } = bulkActions;

  // 計算選取項目的狀態統計
  const selectedJournals = journals?.filter(journal => selectedItems.includes(journal.id)) || [];
  const unpostedSelected = selectedJournals.filter(journal => !journal.isPosted);
  const postedSelected = selectedJournals.filter(journal => journal.isPosted);

  // 自定義批量刪除邏輯：只刪除未過帳的分錄
  const confirmBulkDelete = async () => {
    const unpostedIds = unpostedSelected.map(j => j.id);

    if (unpostedIds.length === 0) {
      toast.error(t('journals.list.noUnpostedSelected'));
      closeBulkDeleteModal();
      return;
    }

    // 先執行樂觀更新（從 store 獲取完整的 pagination）
    const currentState = useJournalStoreBase.getState();
    const optimisticUpdate = createOptimisticBulkDeleteUpdate(useJournalStoreBase.setState, 'journal');
    optimisticUpdate(unpostedIds, currentState.items ?? [], currentState.pagination);

    try {
      // 只刪除未過帳的分錄
      await bulkDeleteJournalEntries(unpostedIds);

      const handler = createBulkDeleteSuccessHandler(
        () => useJournalStoreBase.getState(),
        fetchJournals,
        setCurrentPage
      );
      await handler(unpostedIds);
      refreshAccounts();

      // 根據是否有已過帳分錄顯示不同的成功訊息
      if (postedSelected.length > 0) {
        toast.success(t('journals.list.deletePartialSuccess', { deleted: unpostedIds.length, skipped: postedSelected.length }));
      } else {
        toast.success(t('journals.list.deleteSuccess', { count: unpostedIds.length }));
      }
      closeBulkDeleteModal();

      // 清除選取的未過帳分錄，保留已過帳分錄的選取狀態
      const remainingSelectedIds = selectedItems.filter(id => !unpostedIds.includes(id));
      setSelectedItems(remainingSelectedIds);

    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || '批量刪除失敗';
      toast.error(errorMessage);
    }
  };

  // 批量過帳
  const handleBulkPost = () => {
    if (unpostedSelected.length === 0) {
      toast.error(t('journals.list.selectUnposted'));
      return;
    }
    setShowBulkPostModal(true);
  };

  const confirmBulkPost = async () => {
    if (unpostedSelected.length === 0) return;

    setIsBulkPosting(true);
    try {
      const response = await bulkPostJournalEntries(unpostedSelected.map(j => j.id));

      // 樂觀更新：更新本地狀態
      unpostedSelected.forEach(journal => {
        updateJournal(journal.id, { isPosted: true });
      });

      toast.success(response.data.message || t('journals.list.postSuccess', { count: unpostedSelected.length }));
      setShowBulkPostModal(false);

      // 刷新會計科目數據
      refreshAccounts();

      // 立即刷新儀表板報表數據
      const { useReportStore } = await import('../stores/reportStore');
      useReportStore.getState().fetchReportData(true);

      // 清除選取
      bulkActions.setSelectedItems([]);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || t('journals.list.postError');
      toast.error(errorMessage);
    } finally {
      setIsBulkPosting(false);
    }
  };

  // 批量取消過帳
  const handleBulkUnpost = () => {
    if (postedSelected.length === 0) {
      toast.error(t('journals.list.selectPosted'));
      return;
    }
    setShowBulkUnpostModal(true);
  };

  const confirmBulkUnpost = async () => {
    if (postedSelected.length === 0) return;

    setIsBulkUnposting(true);
    try {
      const response = await bulkUnpostJournalEntries(postedSelected.map(j => j.id));

      // 樂觀更新：更新本地狀態
      postedSelected.forEach(journal => {
        updateJournal(journal.id, { isPosted: false });
      });

      toast.success(response.data.message || t('journals.list.unpostSuccess', { count: postedSelected.length }));
      setShowBulkUnpostModal(false);

      // 刷新會計科目數據
      refreshAccounts();

      // 立即刷新儀表板報表數據
      const { useReportStore } = await import('../stores/reportStore');
      useReportStore.getState().fetchReportData(true);

      // 清除選取
      bulkActions.setSelectedItems([]);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || t('journals.list.unpostError');
      toast.error(errorMessage);
    } finally {
      setIsBulkUnposting(false);
    }
  };


  // 檢查是否有會計科目
  const checkAccountsAvailable = useCallback(async () => {
    try {
      const response = await getAccounts(1, 1);
      const hasAccounts = response.data?.data && response.data.data.length > 0;
      setCanAddJournal(hasAccounts);
    } catch (error) {
      console.error('檢查會計科目失敗:', error);
      setCanAddJournal(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && !isUserLoading) {
      fetchJournals(1);
      checkAccountsAvailable();
    }
  }, [isAuthenticated, isUserLoading, fetchJournals, checkAccountsAvailable]);

  // 監聽記帳分錄總數變化，用於單個刪除時更新 InvoiceLimitBanner
  const prevJournalCountRef = useRef(journals.length);
  useEffect(() => {
    const currentCount = journals.length;
    const prevCount = prevJournalCountRef.current;

    // 如果記帳分錄數量減少（表示有刪除操作），觸發 InvoiceLimitBanner 更新
    if (currentCount < prevCount && prevCount > 0) {
      setLimitBannerRefreshTrigger(prev => prev + 1);
    }

    prevJournalCountRef.current = currentCount;
  }, [journals.length, setLimitBannerRefreshTrigger]);

  const handlePageChange = (newPage: number) => {
    // 先清除選取狀態
    bulkActions.setSelectedItems([]);
    setCurrentPage(newPage);
    fetchJournals(newPage);
  };

  const handleAddJournal = () => {
    if (!canAddJournal) {
      toast.error(t('journals.list.noAccountsError'));
      return;
    }
    if (!canAddInvoice) {
      toast.error(t('journals.list.limitExceeded'));
      return;
    }
    setSelectedJournal(null);
    setIsDialogOpen(true);
  };

  const handleEditJournal = (journal: any) => {
    setSelectedJournal(journal);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedJournal(null);
  };

  const handleJournalCreated = (savedJournal?: any, isEdit?: boolean) => {
    // 樂觀更新已在 JournalDialog 中完成，這裡只處理副作用
    if (savedJournal) {
      if (!isEdit) {
        // 新增記帳分錄後觸發發票限制刷新
        setLimitBannerRefreshTrigger(prev => prev + 1);
      }
      // 清除對應年份的報表快取
      const journalYear = new Date(savedJournal.date).getFullYear().toString();
      import('../stores/reportStore').then(({ useReportStore }) => {
        const reportStore = useReportStore.getState();
        reportStore.clearYearCache(journalYear);
      });
    }
    // 背景刷新確保資料一致性
    fetchJournals(pagination?.currentPage || 1, false);
    checkAccountsAvailable(); // 重新檢查會計科目狀態
    handleCloseDialog();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-TW');
  };

  const formatAmount = (amount: number | string) => {
    return `$${Math.round(Number(amount)).toLocaleString()}`;
  };

  if (isUserLoading) {
    return null;
  }

  if (!isAuthenticated || !currentUserId) {
    return (
      <div className="p-8 text-center text-red-500">
        {t('journals.list.unauthorized')}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      {/* 頁面標題和操作按鈕區 */}
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('journals.list.title')}</h1>
          <div className="flex space-x-3">
            {/* 批量刪除按鈕 - 只在有未過帳分錄被選取時顯示 */}
            {isAdmin(userRole) && unpostedSelected.length > 0 && (
              <BulkActionButton
                selectedCount={unpostedSelected.length}
                loading={isBulkDeleting}
                onClick={handleBulkDelete}
              />
            )}

            {/* 批量過帳按鈕 - 只在有未過帳分錄被選取時顯示 */}
            {unpostedSelected.length > 0 && (
              <button
                onClick={handleBulkPost}
                disabled={isBulkPosting}
                className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2 px-4 rounded transition-all duration-200"
                title={t('journals.list.postTitle', { count: unpostedSelected.length })}
              >
                {isBulkPosting ? t('journals.list.posting') : t('journals.list.postButton', { count: unpostedSelected.length })}
              </button>
            )}

            {/* 批量取消過帳按鈕 - 只在有已過帳分錄被選取時顯示 */}
            {postedSelected.length > 0 && (
              <button
                onClick={handleBulkUnpost}
                disabled={isBulkUnposting}
                className="bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium py-2 px-4 rounded transition-all duration-200"
                title={t('journals.list.unpostTitle', { count: postedSelected.length })}
              >
                {isBulkUnposting ? t('journals.list.unposting') : t('journals.list.unpostButton', { count: postedSelected.length })}
              </button>
            )}



            <button
              onClick={handleAddJournal}
              disabled={!canAddJournal || !canAddInvoice}
              className={`font-bold py-2 px-4 rounded transition-all duration-200 ${
                canAddJournal && canAddInvoice
                  ? 'bg-brand-600 hover:bg-brand-700 text-white cursor-pointer'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              title={
                !canAddJournal
                  ? t('journals.list.noAccountsError')
                  : !canAddInvoice
                  ? t('journals.list.limitExceeded')
                  : undefined
              }
            >
              {t('journals.list.addButton')}
            </button>
          </div>
        </div>
      </div>

      {/* 發票限制橫幅 */}
      <InvoiceLimitBanner onLimitChange={setCanAddInvoice} refreshTrigger={limitBannerRefreshTrigger} />

      {/* 表格上方的分頁控制 */}
      <ListPagination pagination={pagination} onPageChange={handlePageChange} className="mb-3" />

      {/* 分錄表格 */}
      <div className="bg-white dark:bg-slate-800 shadow overflow-hidden rounded-md">
        <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {isLoading ? (
            <TableSkeleton />
          ) : (
            <Table style={{ tableLayout: 'fixed', minWidth: '800px' }}>
              <TableHeader>
                <TableRow>
                  <BulkSelectHeader
                    userRole={userRole}
                    allSelected={allSelected}
                    onToggleAll={toggleSelectAll}
                    hasItems={journals?.length > 0}
                  />
                  <TableHead className="w-20">{t('journals.table.date')}</TableHead>
                  <TableHead className="w-1/3">{t('journals.table.description')}</TableHead>
                  <TableHead className="w-24">{t('common.table.columns.code')}</TableHead>
                  <TableHead className="w-24">{t('common.table.columns.amount')}</TableHead>
                  <TableHead className="w-20">{t('journals.table.status')}</TableHead>
                  <TableHead className="w-44">{t('journals.table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {journals?.map((journal) => (
                  <TableRow key={journal.id}>
                    <BulkSelectCell
                      userRole={userRole}
                      isSelected={selectedItems.includes(journal.id)}
                      onToggle={() => toggleItemSelection(journal.id)}
                      canDelete={canDeleteItem(userRole, journal.userId, currentUserId)}
                    />
                    <TableCell className="w-20 truncate">{formatDate(journal.date)}</TableCell>
                    <TableCell className="w-1/3 font-medium truncate">{journal.description}</TableCell>
                    <TableCell className="w-24 truncate">{journal.reference || '-'}</TableCell>
                    <TableCell className="w-24 truncate">{formatAmount(journal.totalAmount)}</TableCell>
                    <TableCell className="w-20 truncate">
                      {journal.isPosted ? t('journals.status.posted') : t('journals.status.unposted')}
                    </TableCell>
                    <TableCell className="w-44">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditJournal(journal)}
                          className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300"
                        >
                          {t('common.actions.edit')}
                        </button>
                        {journal.isPosted ? (
                          <button
                            onClick={() => unpostJournal(journal.id)}
                            disabled={postingStates[journal.id]}
                            className={`${
                              postingStates[journal.id]
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300'
                            }`}
                          >
                            {postingStates[journal.id] ? t('journals.actions.unposting') : t('journals.actions.unpost')}
                          </button>
                        ) : (
                          <button
                            onClick={() => postJournal(journal.id)}
                            disabled={postingStates[journal.id]}
                            className={`${
                              postingStates[journal.id]
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300'
                            }`}
                          >
                            {postingStates[journal.id] ? t('journals.actions.posting') : t('journals.actions.post')}
                          </button>
                        )}
                        {canDeleteItem(userRole, journal.userId, currentUserId) && (
                          <button
                            onClick={() => deleteJournal(journal.id)}
                            disabled={journal.isPosted}
                            className={`${
                              journal.isPosted
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300'
                            }`}
                            title={journal.isPosted ? t('journals.list.confirmDeleteMessage') : t('common.actions.delete')}
                          >
                            {t('common.actions.delete')}
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                
                {journals?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-gray-500 dark:text-gray-400">
                      {t('journals.list.noJournals')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* 分頁 */}
      <ListPagination pagination={pagination} onPageChange={handlePageChange} className="mt-6" />

      {/* 對話框 */}
      <JournalDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        journal={selectedJournal}
        onJournalCreated={handleJournalCreated}
      />

      {/* 刪除確認對話框 */}
      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title={t('journals.list.confirmDeleteTitle')}
        message={t('journals.list.confirmDeleteMessage')}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      {/* 批量刪除確認對話框 */}
      <ConfirmModal
        isOpen={showBulkDeleteModal}
        onClose={closeBulkDeleteModal}
        onConfirm={confirmBulkDelete}
        title={t('journals.list.confirmBulkDeleteTitle')}
        message={
          postedSelected.length > 0
            ? t('journals.list.confirmBulkDeletePartialMessage', { unposted: unpostedSelected.length, posted: postedSelected.length })
            : t('journals.list.confirmBulkDeleteMessage', { count: unpostedSelected.length })
        }
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isBulkDeleting}
        loadingText={t('common.status.deleting')}
      />

      {/* 批量過帳確認對話框 */}
      <ConfirmModal
        isOpen={showBulkPostModal}
        onClose={() => setShowBulkPostModal(false)}
        onConfirm={confirmBulkPost}
        title={t('journals.list.confirmPostTitle')}
        message={t('journals.list.confirmPostMessage', { count: unpostedSelected.length })}
        confirmText={t('journals.actions.post')}
        cancelText={t('common.actions.cancel')}
        type="success"
        isLoading={isBulkPosting}
        loadingText={t('journals.list.posting')}
      />

      {/* 批量取消過帳確認對話框 */}
      <ConfirmModal
        isOpen={showBulkUnpostModal}
        onClose={() => setShowBulkUnpostModal(false)}
        onConfirm={confirmBulkUnpost}
        title={t('journals.list.confirmUnpostTitle')}
        message={t('journals.list.confirmUnpostMessage', { count: postedSelected.length })}
        confirmText={t('journals.actions.unpost')}
        cancelText={t('common.actions.cancel')}
        type="warning"
        isLoading={isBulkUnposting}
        loadingText={t('journals.list.unposting')}
      />
    </div>
  );
};

export default JournalListPage;